<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    max-width="480"
    persistent
    scrollable
    content-class="quick-task-dialog"
  >
    <v-card
      class="quick-task-card"
      :class="{ 'quick-task-card--technician': technicianMode }"
    >
      <v-card-title class="d-flex align-center pa-3 pb-2">
        <v-icon size="20" color="accent" class="mr-2">mdi-clipboard-plus</v-icon>
        <span class="text-body-1 font-weight-bold">Quick Task</span>
        <v-spacer />
        <v-btn icon size="x-small" variant="text" @click="close">
          <v-icon size="16">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-3">
        <!-- Entity context badge -->
        <div v-if="entityLabel" class="mb-3">
          <v-chip size="small" color="primary" variant="tonal" prepend-icon="mdi-link">
            {{ entityLabel }}
          </v-chip>
        </div>

        <!-- Job Number -->
        <v-text-field
          v-if="jobNumber"
          :model-value="jobNumber"
          label="Job #"
          variant="outlined"
          density="compact"
          readonly
          hide-details
          class="mb-3"
          prepend-inner-icon="mdi-briefcase-outline"
        />

        <!-- Task Type -->
        <div class="d-flex align-center mb-2">
          <div class="text-caption font-weight-bold text-uppercase text-medium-emphasis">Task Type</div>
          <v-spacer />
          <v-btn
            v-if="canManageTypes"
            icon size="x-small" variant="text" class="text-medium-emphasis"
            @click="showTypeSettings = true"
          >
            <v-icon size="16">mdi-cog</v-icon>
            <v-tooltip activator="parent" location="top">Manage Task Types</v-tooltip>
          </v-btn>
        </div>
        <div class="d-flex flex-wrap ga-1 mb-3">
          <v-chip
            v-for="tt in filteredTaskTypes"
            :key="tt.id"
            :color="selectedType?.id === tt.id ? (tt.color || 'accent') : 'default'"
            :variant="selectedType?.id === tt.id ? 'flat' : 'outlined'"
            size="small"
            @click="selectType(tt)"
            class="cursor-pointer"
          >
            <v-icon start size="14" :color="selectedType?.id === tt.id ? undefined : (tt.color || undefined)">
              {{ tt.icon || 'mdi-clipboard-text' }}
            </v-icon>
            {{ tt.name }}
            <v-icon
              v-if="tt.occupies_timeslot"
              end
              size="13"
              :color="selectedType?.id === tt.id ? undefined : 'accent'"
            >mdi-calendar-clock</v-icon>
            <v-tooltip v-if="tt.occupies_timeslot" activator="parent" location="top">
              Occupies a schedule slot
            </v-tooltip>
          </v-chip>
        </div>
        <div class="text-caption text-medium-emphasis mb-2 d-flex align-center ga-1">
          <v-icon size="13" color="accent">mdi-calendar-clock</v-icon>
          = occupies a schedule slot (shows on the schedule)
        </div>

        <!-- Title -->
        <v-text-field
          v-model="form.title"
          label="Title *"
          variant="outlined"
          density="compact"
          :rules="[v => !!v || 'Title is required']"
          :error="submitted && !form.title"
          class="mb-2"
        />

        <!-- Assign to: User/Team + Link Job button -->
        <div class="d-flex align-center ga-2 mb-2">
          <v-btn-toggle v-model="assignMode" mandatory density="compact" color="accent" class="mb-1">
            <v-btn value="user" size="x-small">User</v-btn>
            <v-btn value="team" size="x-small">Team</v-btn>
          </v-btn-toggle>
          <v-spacer />
          <v-btn
            size="x-small"
            color="accent"
            variant="tonal"
            prepend-icon="mdi-briefcase-plus-outline"
            @click="toggleJobSearch"
          >
            {{ linkedJobs.length ? `Jobs (${linkedJobs.length})` : 'Link Job' }}
          </v-btn>
        </div>

        <!-- Link Job search (right under button) -->
        <div v-if="showJobSearch" class="mb-2">
          <v-autocomplete
            v-model="jobSearchPick"
            :items="jobSearchResults"
            :item-title="(j) => `#${j.job_number} — ${j.job_type_name}`"
            item-value="job_number"
            label="Enter job number..."
            variant="outlined"
            density="compact"
            :loading="jobSearchLoading"
            :error="showJobSearch && !linkedJobs.length"
            :messages="showJobSearch && !linkedJobs.length ? ['Select at least one job'] : []"
            no-filter
            return-object
            clearable
            prepend-inner-icon="mdi-magnify"
            @update:search="onJobSearch"
            @update:model-value="onJobPicked"
          >
            <template #item="{ item, props: itemProps }">
              <v-list-item v-bind="itemProps" :subtitle="null">
                <div class="d-flex align-center">
                  <v-chip
                    :color="item.raw.source === 'internal' ? 'primary' : 'warning'"
                    size="x-small"
                    variant="flat"
                    class="mr-2"
                  >
                    {{ item.raw.source === 'internal' ? 'INT' : 'ST' }}
                  </v-chip>
                  <div>
                    <div class="text-body-2 font-weight-medium">#{{ item.raw.job_number }} — {{ item.raw.job_type_name }}</div>
                    <div class="text-caption text-medium-emphasis">
                      <v-icon size="10" class="mr-1">mdi-account-wrench</v-icon>{{ item.raw.technician_name || 'N/A' }}
                      <span class="mx-1">·</span>
                      <v-icon size="10" class="mr-1">mdi-circle</v-icon>{{ item.raw.job_status }}
                      <span v-if="item.raw.scheduled_date" class="mx-1">·</span>
                      <span v-if="item.raw.scheduled_date">{{ formatJobDate(item.raw.scheduled_date) }}</span>
                    </div>
                  </div>
                </div>
              </v-list-item>
            </template>
            <template #no-data>
              <v-list-item v-if="jobSearchLoading">
                <v-list-item-title class="text-medium-emphasis">Searching...</v-list-item-title>
              </v-list-item>
              <v-list-item v-else-if="jobSearchQuery && jobSearchQuery.length >= 2">
                <v-list-item-title class="text-medium-emphasis">No jobs found</v-list-item-title>
              </v-list-item>
              <v-list-item v-else>
                <v-list-item-title class="text-medium-emphasis">Type at least 2 digits</v-list-item-title>
              </v-list-item>
            </template>
          </v-autocomplete>
          <!-- Linked jobs chips -->
          <div v-if="linkedJobs.length" class="d-flex flex-wrap ga-1 mt-1">
            <v-chip
              v-for="(job, idx) in linkedJobs"
              :key="idx"
              size="small"
              :color="job.source === 'internal' ? 'primary' : 'warning'"
              variant="tonal"
              closable
              @click:close="removeLinkedJob(idx)"
            >
              <v-icon start size="14">mdi-briefcase-outline</v-icon>
              #{{ job.job_number }}
              <span class="ml-1 text-caption">{{ job.job_type_name }}</span>
            </v-chip>
          </div>
        </div>

        <!-- User autocomplete (search & add) -->
        <v-autocomplete
          v-if="assignMode === 'user'"
          v-model="userPickerValue"
          :items="availableUsers"
          item-title="display_label"
          item-value="id"
          label="Search & add user... *"
          variant="outlined"
          density="compact"
          clearable
          :error="submitted && assignMode === 'user' && !selectedUserIds.length"
          :error-messages="submitted && assignMode === 'user' && !selectedUserIds.length ? ['Select at least one user'] : []"
          class="mb-2"
          @update:model-value="onUserPicked"
        >
          <template #item="{ item, props: itemProps }">
            <v-list-item v-bind="itemProps">
              <template #prepend>
                <v-avatar size="28" :color="item.raw.avatar_url ? '' : 'primary'" class="mr-2">
                  <v-img v-if="item.raw.avatar_url" :src="item.raw.avatar_url" />
                  <span v-else class="text-on-primary text-caption">{{ getInitials(item.raw) }}</span>
                </v-avatar>
              </template>
              <template #append>
                <v-icon v-if="item.raw.is_online" size="8" color="success">mdi-circle</v-icon>
              </template>
              <v-list-item-subtitle v-if="item.raw.org_unit_name || item.raw.role" class="text-caption">
                {{ item.raw.org_unit_name || '' }}{{ item.raw.org_unit_name && item.raw.role ? ' · ' : '' }}{{ item.raw.role || '' }}
              </v-list-item-subtitle>
            </v-list-item>
          </template>
        </v-autocomplete>

        <!-- Selected user chips -->
        <div v-if="assignMode === 'user' && selectedUsers.length" class="d-flex flex-wrap ga-1 mb-2">
          <v-chip
            v-for="user in selectedUsers"
            :key="user.id"
            size="small"
            color="accent"
            variant="tonal"
          >
            <template #prepend>
              <v-avatar size="20" :color="user.avatar_url ? '' : 'primary'">
                <v-img v-if="user.avatar_url" :src="user.avatar_url" />
                <span v-else class="text-on-primary" style="font-size: 9px;">{{ getInitials(user) }}</span>
              </v-avatar>
            </template>
            {{ user.full_name }}
            <template #append>
              <v-icon size="14" class="ml-1 cursor-pointer" @click="removeUser(user.id)">mdi-close-circle</v-icon>
            </template>
          </v-chip>
        </div>

        <!-- Team select -->
        <v-select
          v-if="assignMode === 'team'"
          v-model="form.assignee_team_id"
          :items="teamsList"
          item-title="name"
          item-value="id"
          label="Assign to Team *"
          variant="outlined"
          density="compact"
          clearable
          :rules="[v => !!v || 'Required']"
          :error="submitted && assignMode === 'team' && !form.assignee_team_id"
          :messages="submitted && assignMode === 'team' && !form.assignee_team_id ? ['Select a team'] : []"
          class="mb-2"
        />

        <!-- Note with voice input -->
        <div class="position-relative mb-2">
          <v-textarea
            v-model="form.description"
            label="Note (optional)"
            variant="outlined"
            density="compact"
            rows="2"
            hide-details
          />
          <div class="voice-btn-wrap">
            <VoiceInput @result="onVoiceResult" />
          </div>
        </div>

        <!-- First comment (optional) -->
        <v-textarea
          v-model="form.first_comment"
          label="Comment (optional)"
          variant="outlined"
          density="compact"
          rows="2"
          hide-details
          class="mb-2"
          prepend-inner-icon="mdi-comment-text-outline"
        />

        <!-- Start / End DateTime -->
        <div class="d-flex ga-2 mb-2">
          <v-text-field
            v-model="form.start_datetime"
            label="Start"
            type="datetime-local"
            variant="outlined"
            density="compact"
            hide-details
            :disabled="form.occupies_timeslot"
            :hint="form.occupies_timeslot ? 'Set by the slot slider below' : ''"
            class="flex-grow-1 datetime-compact"
          />
          <v-text-field
            v-model="form.end_datetime"
            label="End"
            type="datetime-local"
            variant="outlined"
            density="compact"
            hide-details
            :disabled="form.occupies_timeslot"
            class="flex-grow-1 datetime-compact"
          />
        </div>

        <!-- Duration buttons (deadline-style) — disabled for slot tasks, which
             must start & end on the SAME day (use the slot time slider below). -->
        <div class="d-flex align-center ga-2 mb-3">
          <span class="text-caption text-medium-emphasis">Duration:</span>
          <v-btn-toggle
            v-model="activeDuration"
            mandatory
            density="compact"
            color="accent"
            :disabled="form.occupies_timeslot"
          >
            <v-btn value="1d" size="x-small" @click="setDuration('1d')">1 Day</v-btn>
            <v-btn value="3d" size="x-small" @click="setDuration('3d')">3 Days</v-btn>
            <v-btn value="1w" size="x-small" @click="setDuration('1w')">1 Week</v-btn>
          </v-btn-toggle>
          <span v-if="form.occupies_timeslot" class="text-caption text-medium-emphasis">
            slot uses start–end below
          </span>
        </div>

        <!-- Slot time slider (only for slot tasks): drag start & end within the
             SAME day. Length defaults from the task type's slot length. -->
        <div v-if="form.occupies_timeslot" class="mb-3">
          <div class="d-flex justify-space-between align-center text-caption mb-1">
            <span class="font-weight-medium">
              Slot time: {{ fmtMin(slotRange[0]) }} – {{ fmtMin(slotRange[1]) }}
            </span>
            <span class="text-medium-emphasis">{{ slotRange[1] - slotRange[0] }} min</span>
          </div>
          <v-range-slider
            v-model="slotRange"
            :min="0"
            :max="1439"
            :step="15"
            color="accent"
            density="compact"
            hide-details
            strict
          />
        </div>

        <!-- Priority -->
        <div class="d-flex align-center ga-2 mb-3">
          <span class="text-caption text-medium-emphasis">Priority:</span>
          <v-chip-group v-model="form.priority" mandatory>
            <v-chip value="low" size="x-small" variant="outlined" class="text-medium-emphasis">Low</v-chip>
            <v-chip value="medium" size="x-small" variant="outlined" color="info">Medium</v-chip>
            <v-chip value="high" size="x-small" variant="outlined" color="warning">High</v-chip>
            <v-chip value="critical" size="x-small" variant="outlined" color="error">Critical</v-chip>
          </v-chip-group>
        </div>

        <!-- P5: occupies a schedule slot (shows in the technician timeline) -->
        <div class="d-flex align-center justify-space-between mb-3 pa-2 border rounded">
          <div class="d-flex align-center ga-2">
            <v-icon size="20" :color="form.occupies_timeslot ? 'accent' : 'medium-emphasis'">mdi-calendar-clock</v-icon>
            <div>
              <div class="text-body-2 font-weight-medium">Occupies a schedule slot</div>
              <div class="text-caption text-medium-emphasis">
                {{ form.occupies_timeslot ? 'Shows on the technician\'s timeline like a job' : 'Reminder only — not shown on the timeline' }}
              </div>
            </div>
          </div>
          <v-switch v-model="form.occupies_timeslot" color="accent" density="compact" hide-details inset />
        </div>

        <!-- Controller (supervisor) -->
        <v-autocomplete
          v-model="form.controller_user_id"
          :items="usersList"
          item-title="display_label"
          item-value="id"
          label="Controller"
          variant="outlined"
          density="compact"
          hide-details
          :readonly="!canAssignOnBehalf"
          :menu-props="canAssignOnBehalf ? {} : { modelValue: false }"
          @click="onControllerClick"
        >
          <template #prepend-inner>
            <v-avatar size="22" :color="controllerUser?.avatar_url ? '' : 'primary'" class="mr-1">
              <v-img v-if="controllerUser?.avatar_url" :src="controllerUser.avatar_url" />
              <span v-else class="text-on-primary" style="font-size: 9px;">{{ controllerUser ? getInitials(controllerUser) : '?' }}</span>
            </v-avatar>
          </template>
          <template #item="{ item, props: itemProps }">
            <v-list-item v-bind="itemProps">
              <template #prepend>
                <v-avatar size="28" :color="item.raw.avatar_url ? '' : 'primary'" class="mr-2">
                  <v-img v-if="item.raw.avatar_url" :src="item.raw.avatar_url" />
                  <span v-else class="text-on-primary text-caption">{{ getInitials(item.raw) }}</span>
                </v-avatar>
              </template>
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-3 pt-2">
        <v-spacer />
        <v-btn variant="text" size="small" @click="close">Cancel</v-btn>
        <v-btn
          color="accent"
          variant="flat"
          size="small"
          :loading="saving"
          :disabled="saving"
          @click="createTask"
        >
          <v-icon start size="16">mdi-plus</v-icon>
          Create Task
          <span v-if="selectedUsers.length > 1" class="ml-1">({{ selectedUsers.length }})</span>
        </v-btn>
      </v-card-actions>
    </v-card>

    <TaskTypeSettingsDialog
      v-model="showTypeSettings"
      @saved="onTypeSettingsSaved"
    />
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { taskTypesApi, tasksOpsApi } from '@/api/tasks'
import { useNotify } from '@/composables/useNotify'
import { useAuthStore } from '@/stores/auth'
import TaskTypeSettingsDialog from './TaskTypeSettingsDialog.vue'
import VoiceInput from './VoiceInput.vue'
import axios from '@/api/client'

const { notify } = useNotify()
const authStore = useAuthStore()
const canManageTypes = computed(() => authStore.hasPermission('task_types.manage'))
const canAssignOnBehalf = computed(() => authStore.hasPermission('tasks.assign_on_behalf'))
const currentUserId = computed(() => authStore.user?.id)

const props = defineProps({
  modelValue: Boolean,
  entityType: { type: String, default: '' },
  entityId: { type: [Number, String], default: null },
  entityLabel: { type: String, default: '' },
  category: { type: String, default: '' },
  defaultAssigneeTeamId: { type: Number, default: null },
  technicianMode: { type: Boolean, default: false },
  jobNumber: { type: String, default: '' },
  jobTechnicianIds: { type: Array, default: () => [] },
  preSelectedUserId: { type: [Number, String], default: null },
  // When opened from a technician's schedule lane we know the employee (not the
  // user) and the clicked slot time — preselect the assignee and prefill the
  // start/end so the dispatcher doesn't have to re-pick what they clicked.
  preSelectedEmployeeId: { type: [Number, String], default: null },
  // Name of the pre-selected assignee (from the clicked lane). Used to build a
  // fallback chip when the resolved user is not in the assignable list, so the
  // technician never silently "drops off" the new task. TICK-00164.
  preSelectedAssigneeName: { type: String, default: '' },
  preSelectedStart: { type: String, default: '' },
  preSelectedEnd: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'created'])

const saving = ref(false)
const submitted = ref(false)
const taskTypes = ref([])
const usersList = ref([])
const teamsList = ref([])
const selectedType = ref(null)
const assignMode = ref('user')
const showTypeSettings = ref(false)
const activeDuration = ref('1d')
const empToUserMap = ref({})
const userPickerValue = ref(null)
const selectedUserIds = ref([])

// Linked jobs
const showJobSearch = ref(false)
const jobSearchPick = ref(null)
const jobSearchResults = ref([])
const jobSearchLoading = ref(false)
const jobSearchQuery = ref('')
const linkedJobs = ref([])
let jobSearchTimer = null

const selectedUsers = computed(() =>
  selectedUserIds.value
    .map(id => usersList.value.find(u => u.id === id))
    .filter(Boolean)
)

const availableUsers = computed(() =>
  usersList.value.filter(u => !selectedUserIds.value.includes(u.id))
)

function nowLocal() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`
}

function addDays(dateStr, days) {
  const d = new Date(dateStr)
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`
}

// ---- Slot time helpers (datetime-local <-> minutes-of-day) ----
function dtDatePart(s) { return (s || '').slice(0, 10) }
function dtTimeMinutes(s) {
  const t = (s || '').slice(11, 16)
  if (!t) return 0
  const [h, m] = t.split(':').map(Number)
  return (h || 0) * 60 + (m || 0)
}
function buildDt(datePart, minutes) {
  const mm = Math.max(0, Math.min(1439, minutes | 0))
  const h = String(Math.floor(mm / 60)).padStart(2, '0')
  const m = String(mm % 60).padStart(2, '0')
  return `${datePart}T${h}:${m}`
}
function fmtMin(minutes) {
  const mm = Math.max(0, Math.min(1439, minutes | 0))
  let h = Math.floor(mm / 60)
  const m = String(mm % 60).padStart(2, '0')
  const ap = h >= 12 ? 'PM' : 'AM'
  h = ((h + 11) % 12) + 1
  return `${h}:${m} ${ap}`
}

function getInitials(user) {
  const f = (user.first_name || '')[0] || ''
  const l = (user.last_name || '')[0] || ''
  return (f + l).toUpperCase() || '?'
}

const form = ref({
  title: '',
  description: '',
  priority: 'medium',
  assignee_team_id: null,
  start_datetime: '',
  end_datetime: '',
  controller_user_id: null,
  first_comment: '',
  // P5: whether this task takes a real slot on the technician's schedule
  // (shows in the timeline like a job) or is just a reminder. Defaults from
  // the selected task-type badge; can be overridden per task here.
  occupies_timeslot: false,
})

const controllerUser = computed(() =>
  usersList.value.find(u => u.id === form.value.controller_user_id)
)

// Slot time as a [startMin, endMin] range within the SAME day. The slider reads
// and writes form.start_datetime / form.end_datetime keeping the start day, so a
// slot task can never span midnight.
const slotRange = computed({
  get() {
    const s = dtTimeMinutes(form.value.start_datetime)
    let e = dtTimeMinutes(form.value.end_datetime)
    if (!form.value.end_datetime || e <= s) e = Math.min(s + 60, 1439)
    return [s, e]
  },
  set([s, e]) {
    const datePart = dtDatePart(form.value.start_datetime) || dtDatePart(nowLocal())
    if (e <= s) e = Math.min(s + 15, 1439)
    form.value.start_datetime = buildDt(datePart, s)
    form.value.end_datetime = buildDt(datePart, e)
  },
})

function onControllerClick() {
  if (!canAssignOnBehalf.value) {
    notify.info('You don\'t have permission to assign tasks on behalf of another person. Contact your administrator.')
  }
}

const filteredTaskTypes = computed(() => {
  if (!props.category) return taskTypes.value
  return taskTypes.value.filter(t => t.category === props.category || t.category === 'general')
})

// Single source of truth for a task's title so it reads the SAME no matter how
// it was created (type badge, header quick-add, slot click, or from a job):
//   "<Type name>"                              — no job linked
//   "<Type name> - <entity label>"             — opened from a job (has customer)
//   "<Type name> - Job #<n>"                   — a job linked via "Link Job"
function buildJobSuffix() {
  if (props.entityLabel) return ` - ${props.entityLabel}`
  if (linkedJobs.value.length) {
    const j = linkedJobs.value[0]
    return j.customer_name ? ` - #${j.job_number} — ${j.customer_name}` : ` - Job #${j.job_number}`
  }
  return ''
}

// Auto-manage the title. force=true (type change) always rewrites; force=false
// (job linked/unlinked) only rewrites while the user has NOT typed a custom
// title, so manual edits are never clobbered.
const lastAutoTitle = ref('')
function applyAutoTitle(force = false) {
  if (!selectedType.value) return
  if (!force && form.value.title && form.value.title !== lastAutoTitle.value) return
  const auto = `${selectedType.value.name}${buildJobSuffix()}`
  form.value.title = auto
  lastAutoTitle.value = auto
}

function selectType(tt) {
  selectedType.value = tt
  applyAutoTitle(true)
  form.value.priority = tt.default_priority || 'medium'
  // P5: inherit the badge's default "occupies a schedule slot" setting.
  form.value.occupies_timeslot = !!(tt.occupies_timeslot === true || tt.occupies_timeslot === 1)
  // For a slot type, default the END to start + the type's slot length on the
  // SAME day (slot_minutes -> sla_minutes -> 60), so it renders as a real band.
  if (form.value.occupies_timeslot) {
    const mins = tt.slot_minutes || tt.sla_minutes || 60
    const datePart = dtDatePart(form.value.start_datetime) || dtDatePart(nowLocal())
    const s = dtTimeMinutes(form.value.start_datetime)
    form.value.end_datetime = buildDt(datePart, Math.min(s + mins, 1439))
  }
  if (tt.default_team_id) {
    assignMode.value = 'team'
    form.value.assignee_team_id = tt.default_team_id
  }
}

function onUserPicked(userId) {
  if (!userId) return
  if (!selectedUserIds.value.includes(userId)) {
    selectedUserIds.value.push(userId)
    console.log('[QuickTask] Added user:', userId, '-> total:', selectedUserIds.value.length)
  }
  userPickerValue.value = null
}

function removeUser(userId) {
  selectedUserIds.value = selectedUserIds.value.filter(id => id !== userId)
}

function onVoiceResult(text) {
  const current = form.value.description || ''
  form.value.description = current ? `${current} ${text}` : text
  console.log('[QuickTask] Voice input:', text)
}

function onJobSearch(val) {
  jobSearchQuery.value = val || ''
  if (jobSearchTimer) clearTimeout(jobSearchTimer)
  if (!val || val.length < 2) {
    jobSearchResults.value = []
    return
  }
  jobSearchTimer = setTimeout(async () => {
    jobSearchLoading.value = true
    try {
      const { data } = await axios.get('/tasks/search-jobs/', { params: { q: val } })
      const alreadyLinked = new Set(linkedJobs.value.map(j => `${j.source}:${j.job_number}`))
      jobSearchResults.value = (data || []).filter(j => !alreadyLinked.has(`${j.source}:${j.job_number}`))
      console.log('[QuickTask] Job search results:', jobSearchResults.value.length)
    } catch (e) {
      console.error('[QuickTask] Job search error:', e)
      jobSearchResults.value = []
    } finally {
      jobSearchLoading.value = false
    }
  }, 300)
}

function onJobPicked(job) {
  if (!job) return
  linkedJobs.value.push({ ...job })
  jobSearchPick.value = null
  jobSearchResults.value = []
  jobSearchQuery.value = ''
  applyAutoTitle(false)
  console.log('[QuickTask] Linked job:', job.job_number, '-> total:', linkedJobs.value.length)
}

function removeLinkedJob(idx) {
  linkedJobs.value.splice(idx, 1)
  applyAutoTitle(false)
}

function toggleJobSearch() {
  if (showJobSearch.value) {
    if (!linkedJobs.value.length) {
      showJobSearch.value = false
    }
  } else {
    showJobSearch.value = true
  }
}

function formatJobDate(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
}

async function loadData() {
  try {
    const [typesRes, usersRes, teamsRes] = await Promise.all([
      taskTypesApi.list({ active_only: true }),
      axios.get('/planning/tasks/assignable_users/'),
      axios.get('/hr/org-units/'),
    ])
    taskTypes.value = typesRes.data || []

    const rawUsers = usersRes.data.users || usersRes.data || []
    usersList.value = rawUsers.map(u => ({
      ...u,
      is_online: false,
      org_unit_name: u.org_unit_name || '',
      display_label: u.full_name || `${u.first_name} ${u.last_name}`,
    }))

    const rawTeams = teamsRes.data
    let teams = Array.isArray(rawTeams) ? rawTeams : (rawTeams.results || [])
    // Show ALL teams for technicians — no filtering
    teamsList.value = teams

    console.log('[QuickTask] Loaded', taskTypes.value.length, 'types,', usersList.value.length, 'users,', teamsList.value.length, 'teams')
  } catch (e) {
    console.error('[QuickTask] Failed to load data:', e)
  }

  try {
    const [onlineRes, employeesRes] = await Promise.all([
      axios.get('/presence/available/').catch(() => ({ data: [] })),
      axios.get('/hr/employees/?page_size=500').catch(() => ({ data: { results: [] } })),
    ])

    const onlineIds = new Set((onlineRes.data || []).map(u => u.user_id))
    const rawEmp = employeesRes.data
    const employees = Array.isArray(rawEmp) ? rawEmp : (rawEmp.results || [])

    const empByUserId = {}
    const empByEmpId = {}
    for (const e of employees) {
      const uid = e.user_id || e.user
      const eid = e.id || e.employee_id
      if (uid) empByUserId[uid] = e
      if (eid && uid) empByEmpId[eid] = uid
    }
    empToUserMap.value = empByEmpId

    usersList.value.forEach(u => {
      u.is_online = onlineIds.has(u.id)
      const emp = empByUserId[u.id]
      if (emp?.org_unit_name) u.org_unit_name = emp.org_unit_name
      if (emp?.photo_url && !u.avatar_url) u.avatar_url = emp.photo_url
      u.display_label = u.org_unit_name
        ? `${u.full_name} (${u.org_unit_name})`
        : u.full_name
    })
    usersList.value.sort((a, b) => {
      if (a.is_online !== b.is_online) return b.is_online ? 1 : -1
      return (a.full_name || '').localeCompare(b.full_name || '')
    })
  } catch (e) {
    console.warn('[QuickTask] Could not enrich user data:', e)
  }

  if (!form.value.controller_user_id && currentUserId.value) {
    form.value.controller_user_id = currentUserId.value
  }

  // DISABLED: auto-add job technicians to task assignees (may re-enable later)
  // applyJobTechnicians()
}

function applyJobTechnicians() {
  if (!props.jobTechnicianIds?.length) return

  assignMode.value = 'user'
  const resolvedUserIds = props.jobTechnicianIds
    .map(empId => empToUserMap.value[empId] || empId)
    .filter(uid => usersList.value.some(u => u.id === uid))

  selectedUserIds.value = resolvedUserIds

  if (resolvedUserIds.length) {
    console.log('[QuickTask] Auto-assigned technicians:', resolvedUserIds)
  } else {
    console.warn('[QuickTask] Could not resolve technicians. empIds:', props.jobTechnicianIds)
  }
}

async function createTask() {
  submitted.value = true
  const hasAssignee = assignMode.value === 'user'
    ? selectedUserIds.value.length > 0
    : !!form.value.assignee_team_id
  if (!form.value.title || !hasAssignee) return
  saving.value = true
  try {
    // TICK-76: when a task is created from the board/header quick-add or the
    // right-click "New Task" (no job context) but the user linked a job, promote
    // the first linked job to the task's PRIMARY linked entity. This makes the
    // "#job — Customer" label, the job's linked-tasks list, and the technician
    // app all show the link — not just the dispatch bar.
    let linkedEntityType = props.entityType || null
    let linkedEntityId = props.entityId != null ? props.entityId : null
    let linkedEntityLabel = props.entityLabel || null
    if (!linkedEntityType && linkedJobs.value.length) {
      const j = linkedJobs.value[0]
      linkedEntityType = j.source === 'internal' ? 'job' : 'st_job'
      linkedEntityId = String(j.job_id)
      linkedEntityLabel = j.customer_name ? `#${j.job_number} — ${j.customer_name}` : `#${j.job_number}`
    }

    let linkedJobsPayload = linkedJobs.value.length
      ? linkedJobs.value.map(j => ({
          source: j.source,
          job_id: j.job_id,
          job_number: j.job_number,
          job_type_name: j.job_type_name,
          job_status: j.job_status,
          customer_name: j.customer_name,
          technician_name: j.technician_name,
          created_by_name: j.created_by_name,
          scheduled_date: j.scheduled_date,
        }))
      : []
    // When opened from a job card, mirror the primary entity into task_linked_jobs
    // so tasks_by_entity finds the row even if linked_entity_* alone is insufficient.
    if (!linkedJobsPayload.length && linkedEntityType === 'job' && linkedEntityId != null) {
      const custFromLabel = props.entityLabel?.includes(' - ')
        ? props.entityLabel.split(' - ').slice(1).join(' - ')
        : ''
      linkedJobsPayload = [{
        source: 'internal',
        job_id: linkedEntityId,
        job_number: props.jobNumber || '',
        customer_name: custFromLabel,
      }]
    }

    const payload = {
      title: form.value.title,
      description: form.value.description || '',
      priority: form.value.priority,
      task_type_id: selectedType.value?.id || null,
      urgency_level: selectedType.value?.default_urgency || 'normal',
      task_source: 'quick_task',
      linked_entity_type: linkedEntityType,
      linked_entity_id: linkedEntityId,
      linked_entity_label: linkedEntityLabel,
      job_number: props.jobNumber || null,
      linked_jobs: linkedJobsPayload,
      start_datetime: form.value.start_datetime || null,
      end_datetime: form.value.end_datetime || null,
      controller_user_id: form.value.controller_user_id || null,
      occupies_timeslot: form.value.occupies_timeslot,
    }

    if (assignMode.value === 'user' && selectedUserIds.value.length) {
      if (selectedUserIds.value.length === 1) {
        payload.assignee_user_id = selectedUserIds.value[0]
      } else {
        payload.assignee_user_ids = selectedUserIds.value
      }
    }
    if (assignMode.value === 'team' && form.value.assignee_team_id) {
      payload.assignee_team_id = form.value.assignee_team_id
    }

    const { data } = await tasksOpsApi.create(payload)
    console.log('[QuickTask] Created task(s):', data)

    if (form.value.first_comment && form.value.first_comment.trim()) {
      try {
        const taskId = data.id || (data.tasks && data.tasks[0]?.id)
        if (taskId) {
          await tasksOpsApi.addComment(taskId, {
            content: form.value.first_comment.trim()
          })
          console.log('[QuickTask] First comment added to task', taskId)
        }
      } catch (commentErr) {
        console.warn('[QuickTask] Failed to add first comment:', commentErr)
      }
    }

    close()

    if (data.count && data.count > 1) {
      notify.success(`Created ${data.count} tasks: "${data.title}"`)
    } else {
      notify.success(`Task #${data.task_number} "${data.title}" created`)
    }
    emit('created', {
      ...data,
      assignee_user_id: payload.assignee_user_id || null,
      assignee_team_id: payload.assignee_team_id || null,
    })
    // Global signal so every open schedule (dispatch board, week, technician
    // timeline, task list) refreshes this task LIVE without a page reload —
    // works no matter where the task was created (header quick-add, slot menu,
    // job card, etc.).
    window.dispatchEvent(new CustomEvent('tasks-changed', {
      detail: { action: 'created', id: data.id, occupies_timeslot: !!payload.occupies_timeslot },
    }))
  } catch (e) {
    console.error('[QuickTask] Failed to create task:', e)
    notify.error(e.response?.data?.error || 'Failed to create task')
  } finally {
    saving.value = false
  }
}

function setDuration(key) {
  activeDuration.value = key
  const daysMap = { '1d': 1, '3d': 3, '1w': 7 }
  const days = daysMap[key] || 1
  form.value.end_datetime = addDays(form.value.start_datetime || nowLocal(), days)
}

function onTypeSettingsSaved() {
  loadData()
}

function close() {
  emit('update:modelValue', false)
  resetForm()
}

function resetForm() {
  const now = nowLocal()
  form.value = {
    title: '',
    description: '',
    priority: 'medium',
    assignee_team_id: null,
    start_datetime: now,
    end_datetime: addDays(now, 1),
    controller_user_id: currentUserId.value || null,
    first_comment: '',
    occupies_timeslot: false,
  }
  selectedType.value = null
  lastAutoTitle.value = ''
  assignMode.value = 'user'
  activeDuration.value = '1d'
  selectedUserIds.value = []
  userPickerValue.value = null
  submitted.value = false
  showJobSearch.value = false
  linkedJobs.value = []
  jobSearchResults.value = []
  jobSearchQuery.value = ''
  jobSearchPick.value = null
}

watch(() => props.modelValue, async (open) => {
  if (open) {
    resetForm()
    if (props.defaultAssigneeTeamId) {
      assignMode.value = 'team'
      form.value.assignee_team_id = props.defaultAssigneeTeamId
    }
    await loadData()
    // Preselect the assignee: explicit user id wins; otherwise resolve the
    // technician's employee id (from the clicked lane) to its user.
    let uid = props.preSelectedUserId ? Number(props.preSelectedUserId) : null
    if (!uid && props.preSelectedEmployeeId) {
      uid = empToUserMap.value[Number(props.preSelectedEmployeeId)] || null
    }
    // TICK-00178: in the technician app the person creating a task/event is the
    // technician themselves — so when no explicit assignee was passed, default
    // to the current user, the same way a job auto-attaches its technician.
    // The dispatch board always passes an explicit assignee, so it is unaffected.
    if (!uid && props.technicianMode && currentUserId.value) {
      uid = Number(currentUserId.value)
    }
    if (uid) {
      assignMode.value = 'user'
      // TICK-00164: the resolved user may not be in the assignable list (not
      // returned by /planning/tasks/assignable_users/ or beyond the employee
      // page). Without this, selectedUsers (which filters by usersList) drops
      // the chip and the technician silently "slips off". Inject a synthetic
      // option so the pre-selection always sticks and is submittable.
      if (!usersList.value.some(u => u.id === uid)) {
        const label = props.preSelectedAssigneeName || `User #${uid}`
        usersList.value.push({
          id: uid, full_name: label, display_label: label,
          is_online: false, org_unit_name: '',
        })
        console.log('[QuickTask] Injected fallback assignee', uid, label,
          '(not in assignable list) so it does not slip off')
      }
      if (!selectedUserIds.value.includes(uid)) {
        selectedUserIds.value.push(uid)
      }
      console.log('[QuickTask] Pre-selected assignee uid=', uid,
        'from', props.preSelectedUserId ? 'userId' : 'employeeId',
        props.preSelectedEmployeeId)
    } else if (props.preSelectedEmployeeId) {
      // Technician has no linked user account -> cannot be a task assignee by
      // user. Do not silently pretend; leave the dispatcher to pick a team.
      console.warn('[QuickTask] No user for employeeId=',
        props.preSelectedEmployeeId, '- technician has no login; assign by team.')
    }
    // Prefill the slot time the dispatcher clicked on the lane.
    if (props.preSelectedStart) {
      form.value.start_datetime = props.preSelectedStart
      if (props.preSelectedEnd) form.value.end_datetime = props.preSelectedEnd
    }
  }
})

onMounted(() => {
  // Prefetch only for authenticated non-technician sessions: on the login page
  // these calls 401 → token refresh 400 → logout redirect, feeding a reload
  // loop. The modelValue watcher re-runs loadData() on every open anyway.
  if (!authStore.isAuthenticated) return
  if (!window.location.pathname.startsWith('/technician')) loadData()
})
</script>

<style scoped>
.cursor-pointer { cursor: pointer; }
.voice-btn-wrap {
  position: absolute;
  right: 8px;
  top: 8px;
}
.datetime-compact :deep(input[type="datetime-local"]) {
  font-size: 12px !important;
  letter-spacing: -0.3px;
}
@media (max-width: 480px) {
  .datetime-compact :deep(input[type="datetime-local"]) {
    font-size: 11px !important;
    letter-spacing: -0.5px;
    padding: 4px 2px !important;
  }
  .datetime-compact :deep(.v-field__input) {
    padding-inline: 4px !important;
    min-height: 36px !important;
  }
}
</style>

<!-- Not scoped: the dialog content is teleported to the overlay container,
     outside this component's scope. Keeps the modal aligned with the app's
     corporate dialog pattern (fixed header/footer, scrolling body, brand
     surface + radius) so it renders consistently against the overlay scrim
     on every viewport, including the compact technician app. TICK-00090. -->
<style>
.quick-task-dialog {
  /* Never exceed the (possibly small) viewport: the body scrolls, the header
     and actions stay pinned — same behaviour as CreateTicketDialog / QuoteBuilder. */
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}
.quick-task-dialog .quick-task-card {
  background: rgb(var(--v-theme-surface));
  border-radius: var(--brand-radius-lg, 12px);
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.quick-task-dialog .quick-task-card > .v-card-text {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.quick-task-dialog .quick-task-card > .v-card-title,
.quick-task-dialog .quick-task-card > .v-card-actions {
  flex: 0 0 auto;
}
/* Vuetify adds a 24% white selected-state overlay to solid toggle buttons.
   In the technician app that washes the active interface accent into a
   different colour (for example HCP #0848F8 becomes pale indigo). The solid
   background already communicates selection, so keep its exact preset colour. */
.quick-task-dialog .quick-task-card--technician .v-btn-toggle .v-btn--active > .v-btn__overlay {
  opacity: 0 !important;
}
</style>
